import React from 'react';
import {
  Search,
  Star,
  StarHalf,
  Home,
  MessageCircle,
  Users,
  ChevronDown,
  ArrowRight,
} from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';
import AddressSearchInput from '../components/AddressSearchInput';
import FAQ from '../components/FAQ';
import RecentReviews from '../components/RecentReviews';
import { trackPageView } from '../utils/analytics';

interface HomePageProps {
  showReviewModal: boolean;
  setShowReviewModal: (show: boolean) => void;
  onAddressSelect: (address: string, udprn: string) => void;
}

function HomePage({
  showReviewModal,
  setShowReviewModal,
  onAddressSelect,
}: HomePageProps) {
  const navigate = useNavigate();

  // Track page view on component mount
  React.useEffect(() => {
    trackPageView('Home Page', window.location.href);
  }, []);

  const handleLeaveReview = () => {
    navigate('/leave-review');
  };

  return (
    <div className="min-h-screen">
      <header className="bg-black text-white">
        <nav className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <Link to="/" className="flex items-center">
              <img
                src="https://rentreview.io/wp-content/uploads/2025/06/Rent-Review-Logo-1-2.png"
                alt="Fake Review"
                className="h-12"
              />
            </Link>
          </div>
          <a
            href="https://www.instagram.com/rentreviewsuk"
            target="_blank"
            rel="noopener noreferrer"
            className="bg-gradient-to-r from-purple-500 via-pink-500 to-orange-500 text-white px-3 sm:px-6 py-2 sm:py-2.5 rounded-full font-medium text-xs sm:text-sm hover:shadow-lg hover:scale-105 transition-all duration-200 flex items-center gap-1.5 sm:gap-2 whitespace-nowrap"
          >
            <svg className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
            </svg>
            <span className="hidden xs:inline sm:inline">Follow us on Instagram</span>
            <span className="xs:hidden sm:hidden">Follow</span>
          </a>
        </nav>
      </header>

      <div className="relative">
        {/* Background color layer */}
        <div className="bg-gradient-to-br from-blue-50 via-emerald-50 to-orange-50 relative min-h-screen">
          {/* PNG image layer */}
          <div 
            className="absolute inset-0 z-10"
            style={{
              backgroundImage: 'url(https://rentreview.io/wp-content/uploads/2024/02/ytfyt.png)',
              backgroundSize: '90%',
              backgroundPosition: 'center 35%',
              backgroundRepeat: 'no-repeat'
            }}
          />
          
          {/* Content layer */}
          <div className="relative z-20">
            <section className="relative pt-28 sm:pt-32 pb-20">
              <div className="max-w-4xl mx-auto px-4 text-center">
                <div className="relative inline-block">
                  <h1 className="text-5xl sm:text-5xl md:text-[4.75rem] font-bold mb-2 leading-[1.1]">
                    <span className="bg-[#f9b203] px-4 py-1 rounded-full inline-block relative">
                      <img
                        src="https://rentreview.io/wp-content/uploads/2024/02/Highlight_05.png"
                        alt=""
                        className="absolute top-0 left-0 md:left-[-2rem] w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 transform -translate-y-1/2 -translate-x-1/2 -rotate-12"
                      />
                      The UK's
                    </span>
                    {' '}#1<br className="block sm:hidden" /> Rental <br className="hidden sm:block" />Review<br className="block sm:hidden" /> Platform
                    <img
                      src="https://rentreview.io/wp-content/uploads/2025/05/House-2.png"
                      alt=""
                      className="absolute top-0 right-0 md:right-[-2rem] w-8 h-8 sm:w-10 sm:h-10 md:w-12 md:h-12 transform -translate-y-1/2 translate-x-1/2 rotate-12"
                    />
                  </h1>
                </div>
                <p className="text-gray-600 mb-8 font-bold">
                  💬 By Renters, For Renters
                </p>

                <AddressSearchInput
                  placeholder="Search for a property to leave or find a review..."
                  onSelectAddress={(property) => {
                    navigate(
                      `/property/${encodeURIComponent(
                        property.fullAddress
                      )}?udprn=${property.udprn}`
                    );
                  }}
                  className="mb-6 mx-auto"
                />

                <div className="flex justify-center gap-6 mb-16">
                  <div className="flex items-center gap-2 text-gray-700">
                    <img
                      src="https://rentreview.io/wp-content/uploads/2025/08/New-Arrow-Left.png"
                      alt=""
                     className="w-16 h-16 rounded-lg object-contain p-1 -translate-y-3"
                    />
                    <span className="font-bold flex items-center gap-1">
                      <span>✍️</span>
                      <span>Leave a Review</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-700">
                    <span className="font-bold flex items-center gap-1">
                      <span>Browse Reviews</span>
                      <span>🔎</span>
                    </span>
                    <img
                      src="https://rentreview.io/wp-content/uploads/2025/08/New-arrow-right.png"
                      alt=""
                      className="w-16 h-16 rounded-lg object-contain p-1 -translate-y-3"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12 max-w-5xl mx-auto px-4">
                  <div className="bg-white rounded-xl p-8 shadow-lg">
                    <div className="space-y-8">
                      <h2 className="text-3xl md:text-5xl font-bold leading-tight">
                        Take Back <span className="wonky-underline">Control</span>{' '}
                        of Renting
                      </h2>
                      <div className="space-y-2">
                        <p className="text-lg font-medium">
                          🎲 Renting shouldn't feel like rolling the dice.
                        </p>
                        <p className="text-base text-gray-600 mt-4">
                          Now you can finally see what you're really signing up for.
                          Honest reviews from real renters who've been there
                          before you.
                        </p>
                      </div>
                      <ul className="space-y-4 text-base text-gray-600">
                        <li className="flex items-center gap-1.5">
                          <span>🕵️‍♂️ Search Landlords, Agents & Properties</span>
                        </li>
                        <li className="flex items-center gap-1.5">
                          <span>🔎 See the Real Story Behind the Listing</span>
                        </li>
                        <li className="flex items-center gap-1.5">
                          <span>🤗 Help Others by Sharing Your Experience</span>
                        </li>
                        <li className="flex items-center gap-1.5">
                          <span>🌍 Join a Community of Informed Renters</span>
                        </li>
                      </ul>
                    </div>
                  </div>

                  <div className="space-y-6 text-left">
                    <h2 className="text-xl md:text-3xl font-bold mb-6 md:mb-8 text-center">Browse Recent Reviews</h2>
                    <RecentReviews />
                  </div>
                </div>
              </div>
            </section>
          </div>
        </div>

        <section className="bg-black text-white py-20 relative z-10">
          <div className="max-w-5xl mx-auto px-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">
              <div className="bg-[#edae6d] rounded-2xl p-6 md:p-8 md:pr-4 md:pt-16">
                <h2 className="text-black text-5xl md:text-6xl font-extrabold mb-8 md:mb-6">
                  Changing<br />
                  Renting<br />
                  One Review<br />
                  at a Time
                </h2>
                <div className="space-y-3 md:space-y-4 mt-8 md:mt-8">
                  <div className="flex items-center gap-2.5 md:gap-3 text-black">
                    <img src="https://rentreview.io/wp-content/uploads/2025/05/19.png" alt="Damp" className="w-5 h-5 md:w-6 md:h-6 object-contain" />
                    <span className="text-base md:text-lg font-bold">Damp and Mould</span>
                  </div>
                  <div className="flex items-center gap-2.5 md:gap-3 text-black">
                    <img src="https://rentreview.io/wp-content/uploads/2025/05/20.png" alt="Deposit" className="w-5 h-5 md:w-6 md:h-6 object-contain" />
                    <span className="text-base md:text-lg font-bold">Unreturned Deposits</span>
                  </div>
                  <div className="flex items-center gap-2.5 md:gap-3 text-black">
                    <img src="https://rentreview.io/wp-content/uploads/2025/05/21.png" alt="Ghost" className="w-5 h-5 md:w-6 md:h-6 object-contain" />
                    <span className="text-base md:text-lg font-bold">Ghosting Letting Agents</span>
                  </div>
                  <div className="flex items-center gap-2.5 md:gap-3 text-black">
                    <img src="https://rentreview.io/wp-content/uploads/2025/05/22.png" alt="Rat" className="w-5 h-5 md:w-6 md:h-6 object-contain" />
                    <span className="text-base md:text-lg font-bold">Rodents and Pests</span>
                  </div>
                  <div className="flex items-center gap-2.5 md:gap-3 text-black">
                    <img src="https://rentreview.io/wp-content/uploads/2025/05/24.png" alt="Boiler" className="w-5 h-5 md:w-6 md:h-6 object-contain" />
                    <span className="text-base md:text-lg font-bold">Broken Boilers in Winter</span>
                  </div>
                  <div className="flex items-center gap-2.5 md:gap-3 text-black">
                    <img src="https://rentreview.io/wp-content/uploads/2025/05/23.png" alt="Unlivable" className="w-5 h-5 md:w-6 md:h-6 object-contain" />
                    <span className="text-base md:text-lg font-bold">Unlivable Conditions</span>
                  </div>
                  <div className="flex items-center gap-2.5 md:gap-3 text-black">
                    <img src="https://rentreview.io/wp-content/uploads/2025/05/25.png" alt="Noise" className="w-5 h-5 md:w-6 md:h-6 object-contain" />
                    <span className="text-base md:text-lg font-bold">Noisy Neighbours or Party Flats</span>
                  </div>
                </div>
              </div>

              <div className="space-y-8 md:space-y-12 md:pl-8">
                <h2 className="text-3xl md:text-5xl font-bold text-center md:text-left">
                  The Reality Of<br />
                  Renting In The UK
                </h2>
                
                <div className="space-y-6 md:space-y-8">
                  <div className="flex items-center gap-4 md:gap-6">
                    <img 
                      src="https://rentreview.io/wp-content/uploads/2025/04/61-2.png"
                      alt="Pest problems"
                      className="w-[50px] h-[40px] md:w-[70px] md:h-[56px] object-cover rounded-lg flex-shrink-0"
                    />
                    <p className="text-base md:text-xl">Of Renters Said They Had Issues Getting Their Deposit Back In Full Or On Time</p>
                  </div>

                  <div className="flex items-center gap-4 md:gap-6">
                    <img 
                      src="https://rentreview.io/wp-content/uploads/2025/04/26-1.png"
                      alt="Repair requests"
                      className="w-[50px] h-[40px] md:w-[70px] md:h-[56px] object-cover rounded-lg flex-shrink-0"
                    />
                    <p className="text-base md:text-xl">Of Students Said They Had Rats, Mice, Or Pest Infestations During Their Tenancy</p>
                  </div>

                  <div className="flex items-center gap-4 md:gap-6">
                    <img 
                      src="https://rentreview.io/wp-content/uploads/2025/04/10-1.png"
                      alt="Safety concerns"
                      className="w-[50px] h-[40px] md:w-[70px] md:h-[56px] object-cover rounded-lg flex-shrink-0"
                    />
                    <p className="text-base md:text-xl">Of Tenants Said They Felt Afraid To Ask For Repairs In Case Of Rent Increases Or Eviction</p>
                  </div>

                  <div className="flex items-center gap-4 md:gap-6">
                    <img 
                      src="https://rentreview.io/wp-content/uploads/2025/04/38-1.png"
                      alt="Safety concerns"
                      className="w-[50px] h-[40px] md:w-[70px] md:h-[56px] object-cover rounded-lg flex-shrink-0"
                    />
                    <p className="text-base md:text-xl">Of Students Said They Didn't Feel Safe In Their Accommodation</p>
                  </div>
                </div>

                <div className="flex gap-4 justify-center md:justify-start">
                  <button 
                    onClick={handleLeaveReview}
                    className="bg-[#30a86b] text-white px-6 md:px-7 py-3 rounded-xl font-medium text-sm md:text-base hover:bg-[#2a9760] transition-colors"
                  >
                    ✍️ Leave a Review
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-br from-blue-50 via-emerald-50 to-orange-50 py-8 md:py-16 relative z-10">
          <div className="max-w-7xl mx-auto px-4">
            <div className="flex flex-col md:flex-row items-start gap-8 md:gap-12">
              <div className="w-full md:w-1/2 md:pt-20 space-y-6 md:space-y-8 md:pl-48 text-center md:text-left">
                <h2 className="text-5xl md:text-6xl font-bold mb-8 pt-8 md:pt-0">
                  Join 1,000+
                  <br />
                  Renters
                </h2>
                <h3 className="text-3xl font-bold mb-12">
                  Sharing the Truth About
                  <br />
                  Their Homes
                </h3>
                <p className="text-xl text-gray-600 leading-relaxed mb-12">
                  Every review helps someone avoid damp,
                  <br />
                  dodgy landlords, or hidden fees.
                </p>
                <p className="text-xl text-gray-600 font-bold leading-relaxed mb-16">
                  Be part of the movement and make
                  <br />
                  renting fairer for everyone.
                </p>
                <button
                  onClick={handleLeaveReview}
                  className="bg-[#30a86b] text-white px-7 py-3 rounded-xl font-medium text-base hover:bg-[#2a9760] transition-colors flex items-center gap-2 mx-auto md:mx-0 mb-2 md:mb-0"
                >
                  ✍️ Leave a Review
                </button>
                <div className="hidden md:block md:pb-16 md:mb-0 mb-2 md:mb-0"></div>
              </div>
              <div className="w-full md:w-1/2 md:flex md:items-end">
                <img
                  src="https://rentreview.io/wp-content/uploads/2025/08/fake-Review.png"
                  alt="Fake Review"
                  className="w-full md:w-[400%] h-auto object-contain md:transform md:translate-x-[-10%]"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="bg-gradient-to-br from-blue-50 via-emerald-50 to-orange-50 relative z-10 -mt-8 md:-mt-16 pt-8 md:pt-0">
          <FAQ />
        </section>
      </div>
    </div>
  );
}

export default HomePage;